/**
 * Menu de ações do lead no modal (estilo Kommo)
 */

import { useState } from 'react';
import ExcelJS from 'exceljs';
import { Dropdown, Modal, message } from 'antd';
import type { MenuProps } from 'antd';
import { Hash, MoreHorizontal, Printer, Trash2 } from 'lucide-react';
import { useDeleteCard } from '../hooks/usePipelineCard';

function ExcelIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden>
      <rect x="3" y="3" width="18" height="18" rx="2" fill="#217346" />
      <path
        d="M7 7h4v4H7V7zm0 5h4v4H7v-4zm5-5h4v4h-4V7zm0 5h4v4h-4v-4z"
        fill="#fff"
        opacity="0.9"
      />
    </svg>
  );
}

export interface LeadExportRow {
  campo: string;
  valor: string;
}

interface PipelineLeadActionsMenuProps {
  cardId?: string;
  fileName: string;
  exportRows: LeadExportRow[];
  onManageTags: () => void;
  onDeleted?: () => void;
}

export function PipelineLeadActionsMenu({
  cardId,
  fileName,
  exportRows,
  onManageTags,
  onDeleted,
}: PipelineLeadActionsMenuProps) {
  const deleteCard = useDeleteCard();
  const [deleteOpen, setDeleteOpen] = useState(false);

  const handlePrint = () => {
    window.print();
  };

  const handleExportExcel = async () => {
    try {
      const workbook = new ExcelJS.Workbook();
      const sheet = workbook.addWorksheet('Lead');
      sheet.columns = [
        { header: 'Campo', key: 'campo', width: 28 },
        { header: 'Valor', key: 'valor', width: 48 },
      ];
      exportRows.forEach((row) => sheet.addRow(row));

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${fileName}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      message.success('Exportado para Excel');
    } catch (e) {
      console.error(e);
      message.error('Erro ao exportar');
    }
  };

  const handleDelete = () => {
    if (!cardId) {
      message.warning('Salve o lead antes de excluir');
      return;
    }
    setDeleteOpen(true);
  };

  const confirmDelete = async () => {
    if (!cardId) return;

    try {
      await deleteCard.mutateAsync(cardId);
      message.success('Lead excluído');
      setDeleteOpen(false);
      onDeleted?.();
    } catch {
      message.error('Não foi possível excluir o lead');
    }
  };

  const menuItems: MenuProps['items'] = [
    {
      key: 'print',
      label: 'Imprimir',
      icon: <Printer className="w-4 h-4 text-gray-600" />,
    },
    {
      key: 'tags',
      label: 'Gerenciar tags',
      icon: <Hash className="w-4 h-4 text-gray-600" />,
    },
    {
      key: 'excel',
      label: 'Exportar para o Excel',
      icon: <ExcelIcon className="w-4 h-4 shrink-0" />,
    },
    { type: 'divider' },
    {
      key: 'delete',
      label: <span className="text-[#e57373]">Excluir lead</span>,
      icon: <Trash2 className="w-4 h-4 text-[#e57373]" />,
      disabled: !cardId || deleteCard.isPending,
    },
  ];

  const handleMenuClick: MenuProps['onClick'] = ({ key }) => {
    switch (key) {
      case 'print':
        handlePrint();
        break;
      case 'tags':
        onManageTags();
        break;
      case 'excel':
        void handleExportExcel();
        break;
      case 'delete':
        handleDelete();
        break;
    }
  };

  return (
    <>
      <Dropdown
        menu={{ items: menuItems, onClick: handleMenuClick }}
        trigger={['click']}
        placement="bottomRight"
        classNames={{ root: 'pipeline-lead-actions-dropdown' }}
        styles={{ root: { zIndex: 2000 } }}
      >
        <button
          type="button"
          className="p-1.5 rounded hover:bg-white/10 text-white/70 hover:text-white transition flex-shrink-0"
          aria-label="Mais opções"
          onClick={(e) => e.stopPropagation()}
        >
          <MoreHorizontal className="w-4 h-4" />
        </button>
      </Dropdown>

      <Modal
        open={deleteOpen}
        onCancel={() => setDeleteOpen(false)}
        footer={null}
        destroyOnHidden
        width={400}
        zIndex={2000}
        centered
        className="pipeline-submodal"
        title={
          <span className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-white">
            <Trash2 className="w-4 h-4 text-[#e57373]" />
            Excluir lead
          </span>
        }
      >
        <p className="text-[13px] text-[#8f9aab] dark:text-gray-400 leading-relaxed">
          O lead será removido do kanban. Esta ação não pode ser desfeita.
        </p>

        <div className="flex items-center justify-end gap-2 mt-6 pt-4 border-t border-[#eceef2] dark:border-zinc-800">
          <button
            type="button"
            onClick={() => setDeleteOpen(false)}
            disabled={deleteCard.isPending}
            className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-zinc-800 transition-colors disabled:opacity-60"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={() => void confirmDelete()}
            disabled={deleteCard.isPending}
            className="px-4 py-2.5 rounded-lg text-sm font-semibold text-white bg-[#e57373] hover:bg-[#d66565] disabled:opacity-60 transition-colors"
          >
            {deleteCard.isPending ? 'Excluindo...' : 'Excluir lead'}
          </button>
        </div>
      </Modal>
    </>
  );
}

export default PipelineLeadActionsMenu;
